import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import useUser from '@/features/auth/hooks/useUser'
import { useAuthContext } from '@/features/auth/context'

import dayjs from 'dayjs'
import { format } from 'date-fns'
import useTranslation from '@/hooks/useTranslation'

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]
const LONG_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const LONG_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
const DATE_SUFFIX = ['st', 'nd', 'rd']

interface DateTZUTC {
  year: number
  month: number
  date: number
  day: number
  hour: number
  min: number
  sec: number
}

export const EasyDate = {
  now: () => new Date(),
  oneHourAgo: () => new Date(Date.now() - 60 * 60 * 1000),
  oneDayAgo: () => new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
  sevenDaysAgo: () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  thirtyDaysAgo: () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
  startOfDate: (date: Date, timezone: string = 'UTC') => {
    return dayjs(date).tz(timezone).startOf('day').utc().format()
  },
  endOfDate: (date: Date, timezone: string = 'UTC') => {
    return dayjs(date).tz(timezone).endOf('day').utc().format()
  },
}

type DateTZOptions =
  | 'full'
  | 'timefull'
  | 'datefull'
  | 'timeshort'
  | 'dateshort'
  | 'customWeather'
  | 'separatedDate'
  | 'flowLog'
  | 'weatherChart'
  | 'flowChart'
  | 'programChart'
  | 'do'
  | 'th'
export const useDateTime = () => {
  const { data: user } = useUser()
  const { interfaceId } = useAuthContext()
  const { t, currentLanguage } = useTranslation()
  const SHORT_DAYS = [
    t('shortDaySun'),
    t('shortDayMon'),
    t('shortDayTue'),
    t('shortDayWed'),
    t('shortDayThu'),
    t('shortDayFri'),
    t('shortDaySat'),
  ]

  const { data: dataInterface } = useSearchInterfaceId(interfaceId + '')

  const convertToUTC = (dateTime: string, timeZone: string): string => {
    // Kiểm tra định dạng múi giờ
    const timeZoneMatch = /^GMT([+-])(\d{2}):(\d{2})$/.exec(timeZone)
    if (!dateTime) {
      return ''
    }
    if (!timeZoneMatch) {
      throw new Error('Invalid time zone format. Use "GMT±HH:MM".')
    }

    const offsetSign = timeZoneMatch[1] === '+' ? 1 : -1
    const offsetHours = parseInt(timeZoneMatch[2], 10)
    const offsetMinutes = parseInt(timeZoneMatch[3], 10)
    const totalOffsetMinutes = (offsetHours * 60 + offsetMinutes) * offsetSign

    // Tạo đối tượng Date từ dateTime
    const dateTimeISO = dateTime.endsWith('Z') ? dateTime : dateTime + 'Z'
    const localDate = new Date(dateTimeISO) // Không cần thêm 'Z' nếu đã chuẩn hóa

    if (isNaN(localDate.getTime())) {
      throw new Error('Invalid date time format.')
    }

    // Chuyển đổi thời gian cục bộ về UTC
    const utcDate = new Date(
      localDate.getTime() - totalOffsetMinutes * 60 * 1000,
    )

    // Trả về thời gian UTC dưới định dạng yyyy-MM-ddTHH:mm:ss
    const pad = (num: number) => num.toString().padStart(2, '0')
    return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(
      utcDate.getUTCDate(),
    )}T${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:${pad(
      utcDate.getUTCSeconds(),
    )}`
  }

  const formatTimeZone = (timeZone: string): string => {
    // Kiểm tra và phân tích múi giờ
    const timeZoneMatch = /^GMT([+-]\d{2}):(\d{2})$/.exec(timeZone)

    if (!timeZoneMatch) {
      throw new Error(
        'Invalid time zone format. Use "GMT±HH:MM" or "GMTHH:MM".',
      )
    }

    const offsetSign = timeZoneMatch[1].charAt(0) // + hoặc -
    const offsetHours = timeZoneMatch[1].slice(1) // 04
    const offsetMinutes = timeZoneMatch[2] // 00

    // Định dạng lại múi giờ
    return `GMT${offsetSign}${offsetHours}:${offsetMinutes}`
  }

  const extractDayAndMonth = (
    utcDateTime: string,
  ): { day: number; month: number; hour: number; minutes: number } => {
    let dateString = utcDateTime

    if (dateString.includes(' ') && !dateString.includes('T')) {
      dateString = dateString.replace(' ', 'T')
    }

    const timePart = dateString.split('T')[1]
    if (timePart && timePart.length === 5) {
      dateString = dateString.concat(':00') // Thêm giây nếu thiếu
    }

    if (!dateString.endsWith('Z')) {
      dateString += 'Z'
    }

    const date = new Date(dateString)

    if (isNaN(date.getTime())) {
      throw new Error('Invalid date time format.')
    }

    // Trích xuất ngày, tháng, giờ và phút từ đối tượng Date
    const day = date.getUTCDate()
    const month = date.getUTCMonth() + 1 // Tháng trong JavaScript là 0-indexed
    const hour = date.getUTCHours()
    const minutes = date.getUTCMinutes()

    return { day, month, hour, minutes }
  }

  const convertByGMT = (UTCdate: string, gmt: number) => {
    if (!UTCdate) return new Date()
    if (gmt < -12 || gmt > 14) return new Date(UTCdate) //UTC tương đương với GMT0
    const uxGMT0 = Math.floor(Date.parse(UTCdate) / 1000) // đổi UTC ra unix
    const uxAfterGMT = uxGMT0 + 3600 * gmt // cộng trừ giờ theo gmt input
    return new Date(uxAfterGMT * 1000)
  }

  const convertToUTCSimulation = (
    dateString: string | Date,
    gmt: number,
  ): DateTZUTC | null => {
    if (
      gmt === undefined ||
      gmt === null ||
      isNaN(gmt) ||
      gmt < -12 ||
      gmt > 14
    ) {
      return null
    }

    let date = new Date(dateString) // Ensure date is a Date object

    const plusHour = Math.floor(gmt) // Get the integer part of GMT (hours)
    const plusMin = (gmt % 1) * 60 // Get the fractional part (minutes)

    // Update the UTC hours and minutes by adding the offset
    date.setUTCHours(date.getUTCHours() + plusHour)
    date.setUTCMinutes(date.getUTCMinutes() + plusMin)

    // Extract updated UTC date components
    const y = date.getUTCFullYear()
    const m = date.getUTCMonth()
    const d = date.getUTCDate()
    const D = date.getUTCDay()
    const h = date.getUTCHours()
    const M = date.getUTCMinutes()
    const s = date.getUTCSeconds()

    // Return date components as an object
    return { year: y, month: m, date: d, day: D, hour: h, min: M, sec: s }
  }

  // The date and time parts (already on the interface's clock) in one of
  // the simulation formats
  const formatSimulationParts = (
    utcDate: DateTZUTC | null,
    options: DateTZOptions,
  ) => {
    if (utcDate) {
      const time = {
        hour: utcDate.hour,
        min: utcDate.min,
        sec: utcDate.sec,
      }

      switch (options) {
        case 'do':
          return `${dateStringFormat(utcDate.date, true, false)}`
        case 'th':
          return `${dateWeatherStringFormat(utcDate.date, true, false)}`
        case 'weatherChart':
          return `${utcDate.date} ${monthStringFormat(utcDate.month, true)}, ${
            utcDate.year
          } / ${timeStringFormat(time, false, true)} `
        case 'programChart':
          const programChartdate = Number(prefixZero(utcDate.date))
          const programChartmonth = Number(prefixZero(utcDate.month))
          const programChartDate = new Date(
            utcDate.year,
            programChartmonth,
            programChartdate,
          )
          const formatter = t('programChartTimeFormatter')
          const formattedDate = format(programChartDate, formatter)
          return `${formattedDate} ${timeStringFormat(time, true, false)}`
        case 'flowChart':
          const flowChartDate = new Date(
            utcDate.year,
            utcDate.month,
            utcDate.date,
          )
          const flowChartformatter = t('flowChartTimeFormatter')
          const flowChartformattedDate = format(
            flowChartDate,
            flowChartformatter,
          )

          return `${dayStringFormat(
            utcDate.day,
            true,
          )}, ${flowChartformattedDate}, ${timeStringFormat(
            time,
            true,
            false,
          )} `
        case 'customWeather':
          return `${dayStringFormat(utcDate.day, true)}, ${dateStringFormat(
            utcDate.date,
            true,
            false,
          )} ${monthStringFormat(utcDate.month, true)} ${utcDate.year}`
        case 'timefull':
          return `${timeStringFormat(time, true, false)}`
        case 'timeshort':
          return `${timeStringFormat(time, false, true)}`
        case 'datefull':
          return `${dateStringFormat(utcDate.date, false, true)}/${prefixZero(
            utcDate.month + 1,
          )}/${yearStringFormat(utcDate.year)}`
        case 'dateshort':
          return `${dateStringFormat(utcDate.date)} ${monthStringFormat(
            utcDate.month,
            true,
          )}`
        case 'separatedDate':
          return `${dateStringFormat(utcDate.date)}#${monthStringFormat(
            utcDate.month,
            true,
          )}#${prefixZero(utcDate.hour)}#${prefixZero(utcDate.min)}`
        case 'flowLog':
          return `${utcDate?.year}-${prefixZero(
            utcDate?.month + 1,
          )}-${dateStringFormat(utcDate.date, false, true)}T${timeStringFormat(
            time,
            true,
            false,
          )}+00:00`
        default:
          return `${dateStringFormat(utcDate.date, false, true)}/${prefixZero(
            utcDate?.month + 1,
          )}/${utcDate?.year} ${timeStringFormat(time, true, false)}`
      }
    }
    return ''
  }

  const formatDateSimulation = (
    dateISOString: string,
    options: DateTZOptions = 'full',
    convertZone: boolean = true,
  ) => {
    if (!dateISOString) return ''
    const gmt = parseInt(
      (dataInterface?.timeZone ?? '').replace('GMT', '').replace('+', ''),
    )

    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''
    const utcDate = convertToUTCSimulation(
      dateISOString,
      convertZone ? gmt : -(new Date().getTimezoneOffset() / 60),
    )

    return formatSimulationParts(utcDate, options)
  }

  const convertToUTCByTimezone = (
    dateString: string | Date,
    gmt: number,
  ): DateTZUTC | null => {
    if (
      (gmt === undefined ||
        gmt === null ||
        isNaN(gmt) ||
        gmt < -12 ||
        gmt > 14) &&
      dataInterface
    )
      return null
    const plusHour = Math.floor(gmt / 1)
    const plusMin = (gmt % 1) * 60
    let date = dateString as Date
    if (typeof dateString === 'string') {
      // if (!dateString.includes('Z')) dateString += 'Z'
      date = new Date(dateString)
    }
    const currentH = date.getUTCHours()
    date.setUTCHours(currentH + plusHour)
    const currentM = dataInterface?.timeZone.includes('30')
      ? date.getUTCMinutes() + 30
      : date.getUTCMinutes()
    date.setUTCMinutes(currentM + plusMin)
    const y = date.getUTCFullYear()
    const m = date.getUTCMonth()
    const d = date.getUTCDate()
    const D = date.getUTCDay()
    const h = date.getUTCHours()
    const M = date.getUTCMinutes()
    const s = date.getUTCSeconds()
    return { year: y, month: m, date: d, day: D, hour: h, min: M, sec: s }
  }

  const formatDateByTimezone = (
    dateISOString: string,
    options: DateTZOptions = 'full',
    convertZone: boolean = true,
  ) => {
    if (!dateISOString) return ''
    const gmt = parseInt(
      (dataInterface?.timeZone ?? '').replace('GMT', '').replace('+', ''),
    )

    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''
    const utcDate = convertToUTCByTimezone(
      dateISOString,
      convertZone ? gmt : -(new Date().getTimezoneOffset() / 60),
    )

    if (utcDate) {
      const time = {
        hour: utcDate.hour,
        min: utcDate.min,
        sec: utcDate.sec,
      }

      switch (options) {
        case 'do':
          return `${dateStringFormat(utcDate.date, true, false)}`
        case 'th':
          return `${dateWeatherStringFormat(utcDate.date, true, false)}`
        case 'weatherChart':
          const weatherChartDate = new Date(
            utcDate.year,
            utcDate.month,
            utcDate.date,
          )
          const weatherChartFormatter = t('weatherDashboardTimeFormatter')
          const weatherChartFormattedDate = format(
            weatherChartDate,
            weatherChartFormatter,
          )
          return `${weatherChartFormattedDate} ${timeStringFormat(
            time,
            false,
            true,
          )}`
        case 'programChart':
          return `${prefixZero(utcDate.date)}/${prefixZero(
            utcDate.month + 1,
          )}/${utcDate.year}  ${timeStringFormat(time, true, false)} `
        case 'flowChart':
          return `${dayStringFormat(utcDate.day, true)}, ${dateStringFormat(
            utcDate.date,
            true,
            false,
          )}  ${monthStringFormat(utcDate.month, true)} ${
            utcDate.year
          }, ${timeStringFormat(time, true, false)} `
        case 'customWeather':
          return `${dayStringFormat(utcDate.day, true)}, ${dateStringFormat(
            utcDate.date,
            true,
            false,
          )} ${monthStringFormat(utcDate.month, true)} ${utcDate.year}`
        case 'timefull':
          return `${timeStringFormat(time, true, false)}`
        case 'timeshort':
          return `${timeStringFormat(time, false, true)}`
        case 'datefull':
          return `${dateStringFormat(utcDate.date, false, true)}/${prefixZero(
            utcDate.month + 1,
          )}/${yearStringFormat(utcDate.year)}`
        case 'dateshort':
          return `${dateStringFormat(utcDate.date)} ${monthStringFormat(
            utcDate.month,
            true,
          )}`
        case 'separatedDate':
          return `${dateStringFormat(utcDate.date)}#${monthStringFormat(
            utcDate.month,
            true,
          )}#${prefixZero(utcDate.hour)}#${prefixZero(utcDate.min)}`
        case 'flowLog':
          return `${utcDate?.year}-${prefixZero(
            utcDate?.month + 1,
          )}-${dateStringFormat(utcDate.date, false, true)}T${timeStringFormat(
            time,
            true,
            false,
          )}+00:00`
        default:
          const defaultdate = Number(
            dateStringFormat(utcDate.date, false, true),
          )
          const defaultMonth = Number(prefixZero(utcDate?.month))
          const defaultDate = new Date(utcDate.year, defaultMonth, defaultdate)
          const defaultFormatter = t('defaultTimeFormatter')
          const defaultFormattedDate = format(defaultDate, defaultFormatter)
          return `${defaultFormattedDate} ${timeStringFormat(
            time,
            true,
            false,
          )}`
      }
    }
    return ''
  }

  const formatDateTimeWithoutInterfaceId = (
    dateISOString: string,
    options: DateTZOptions = 'full',
    gmtTimeZoneUser?: any,
  ) => {
    if (!dateISOString) return ''
    const gmt = parseInt(gmtTimeZoneUser?.replace('GMT', '').replace('+', ''))

    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''
    const utcDate = convertToUTCByTimezone(dateISOString, gmt)

    if (utcDate) {
      const time = {
        hour: utcDate.hour,
        min: utcDate.min,
        sec: utcDate.sec,
      }

      switch (options) {
        case 'do':
          return `${dateStringFormat(utcDate.date, true, false)}`
        default:
          return `${dateStringFormat(utcDate.date, false, true)}/${prefixZero(
            utcDate?.month + 1,
          )}/${utcDate?.year} ${timeStringFormat(time, true, false)}`
      }
    }
  }

  //for render
  const convertDateTime = (date: string) => {
    if (!date) return ''
    const gmt = parseInt(
      (dataInterface?.timeZone ?? '').replace('GMT', '').replace('+', ''),
    )
    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''
    const newDate = new Date(date)
    const currentClientHour = newDate.getHours()
    newDate.setHours(currentClientHour + gmt)

    return newDate
  }
  //for submits
  const revertDateTime = (date: Date) => {
    if (!date) return ''
    const gmt = parseInt(
      (dataInterface?.timeZone ?? '').replace('GMT', '').replace('+', ''),
    )
    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''

    const currentClientHour = date.getHours()
    date.setHours(currentClientHour - gmt)

    const newDate = {
      y: date.getFullYear(),
      m: date.getMonth(),
      d: date.getDate(),
      H: date.getHours(),
      M: date.getMinutes(),
      s: date.getSeconds(),
    }
    return `${yearStringFormat(newDate.y)}-${prefixZero(
      newDate.m + 1,
    )}-${prefixZero(newDate.d)}T${prefixZero(newDate.H)}:${prefixZero(
      newDate.M,
    )}:${prefixZero(newDate.s)}Z`
  }
  const prefixZero = (num: number): string => {
    if (num < 0) return ''
    if (num >= 0 && num < 10) return `0${num}`
    return `${num}`
  }
  const timeStringFormat = (
    time: { hour: number; min: number; sec: number },
    is24: boolean = false,
    isShort: boolean = false,
  ): string => {
    if (is24) {
      return isShort
        ? `${prefixZero(time.hour)}:${prefixZero(time.min)}`
        : `${prefixZero(time.hour)}:${prefixZero(time.min)}:${prefixZero(
            time.sec,
          )}`
    }
    const hour12 = time.hour > 12 ? time.hour - 12 : time.hour
    const suffix = time.hour < 12 ? 'AM' : 'PM'
    return isShort
      ? `${prefixZero(hour12)}:${prefixZero(time.min)} ${suffix}`
      : `${prefixZero(hour12)}:${prefixZero(time.min)}:${prefixZero(
          time.sec,
        )} ${suffix}`
  }
  const dayStringFormat = (day: number, isShort: boolean = false): string => {
    if (day < 0 && day > 6) return ''
    return isShort ? `${SHORT_DAYS[day]}` : `${LONG_DAYS[day]}`
  }

  const dateWeatherStringFormat = (
    date: number,
    isHasSuffix: boolean = false,
    isHasPrefix0: boolean = true,
  ): string => {
    if (date < 1 && date > 31) return ''

    let result = ''
    result += isHasPrefix0 ? prefixZero(date) : `${date}`
    if (isHasSuffix) {
      let ds = date !== 11 ? (date % 10) - 1 : date - 1
      result += ds < 3 && ds >= 0 ? DATE_SUFFIX[ds] : `th`
    }

    return result
  }

  const dateStringFormat = (
    date: number,
    isHasSuffix: boolean = false,
    isHasPrefix0: boolean = true,
  ): string => {
    if (date < 1 && date > 31) return ''

    let result = ''
    result += isHasPrefix0 ? prefixZero(date) : `${date}`
    if (isHasSuffix) {
      let ds = date !== 11 ? (date % 10) - 1 : date
      result += ds < 3 && ds >= 0 ? DATE_SUFFIX[ds] : `th`
    }

    return result
  }
  const monthStringFormat = (
    month: number,
    isShort: boolean = false,
  ): string => {
    if (month < 0 && month > 11) return ''
    return isShort ? `${SHORT_MONTHS[month]}` : `${LONG_MONTHS[month]}`
  }
  const yearStringFormat = (year: number, isShort: boolean = false): string => {
    if (year < 0) return ''
    return isShort ? `${year.toString().slice(2, 4)}` : `${year}`
  }

  const convertLocalToProfileZone = (
    date: Date | string | number = new Date(),
  ) => {
    try {
      const inputDate = new Date(date)
      if (isNaN(inputDate.getTime())) return new Date()
      const time = inputDate.getTime()
      const localOffset = inputDate.getTimezoneOffset()
      const timeZoneStr = dataInterface?.timeZone || 'GMT+0'
      const regex = /GMT([+-])(\d{1,2})(?::(\d{2}))?/
      const match = timeZoneStr.match(regex)
      if (!match) return inputDate
      const sign = match[1] === '+' ? 1 : -1
      const hours = parseInt(match[2], 10)
      const minutes = match[3] ? parseInt(match[3], 10) : 0
      const profileOffset = sign * (hours * 60 + minutes)
      const newDate = new Date(time + (localOffset + profileOffset) * 60000)
      return newDate
    } catch (error) {
      return new Date()
    }
  }

  // The interface's time zone by name, so times follow its daylight saving;
  // without a name, a fixed zone from its GMT offset (whole hours only)
  const simulationTimeZone = (() => {
    if (dataInterface?.timeZoneOriginId) return dataInterface.timeZoneOriginId
    const match = dataInterface?.timeZone?.match(/GMT([+-])(\d{2}):?(\d{2})?/)
    if (!match) return undefined
    const hours = parseInt(match[2], 10)
    if (!hours) return 'UTC'
    // Etc zones count the other way: Etc/GMT-10 is GMT+10
    return `Etc/GMT${match[1] === '+' ? '-' : '+'}${hours}`
  })()

  // A real time on the interface's clock (with that day's daylight saving),
  // in one of the simulation formats
  const formatSimulationTime = (
    time: Date | string | number | undefined | null,
    options: DateTZOptions = 'full',
  ) => {
    if (time === undefined || time === null || !simulationTimeZone) return ''
    const local = dayjs.tz(new Date(time).getTime(), simulationTimeZone)
    if (!local.isValid()) return ''
    // the zone's wall clock, read as UTC parts
    const wall = new Date(local.valueOf() + local.utcOffset() * 60 * 1000)
    return formatSimulationParts(
      {
        year: wall.getUTCFullYear(),
        month: wall.getUTCMonth(),
        date: wall.getUTCDate(),
        day: wall.getUTCDay(),
        hour: wall.getUTCHours(),
        min: wall.getUTCMinutes(),
        sec: wall.getUTCSeconds(),
      },
      options,
    )
  }

  // the interface's UTC offset at a time, e.g. "GMT+11" (tells the two
  // copies of an hour apart when clocks go back)
  const simulationOffsetLabel = (time: number) => {
    if (!simulationTimeZone) return ''
    const minutes = dayjs.tz(time, simulationTimeZone).utcOffset()
    const hours = Math.trunc(Math.abs(minutes) / 60)
    const rest = Math.abs(minutes) % 60
    return `GMT${minutes < 0 ? '-' : '+'}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`
  }

  const convertLocalSimulation = (
    date: Date | string | number = new Date(),
  ) => {
    const time = new Date(date).getTime()
    const localOffset = new Date(date).getTimezoneOffset() / 60 // Local time offset in hours

    const match = dataInterface?.timeZone.match(/GMT([+-])(\d{2}):?(\d{2})?/)

    if (!match) {
      throw new Error('Invalid time zone format')
    }

    const sign = match[1] === '+' ? 1 : -1
    const hours = parseInt(match[2], 10)
    const minutes = parseInt(match[3] || '0', 10) // Handle cases where minutes might be missing

    // Calculate the total offset in hours, considering both hours and minutes
    const gmtOffset = sign * (hours + minutes / 60)

    // Adjust time by local offset and the profile's GMT offset
    const newDate = time + (localOffset + gmtOffset) * 3600000

    return new Date(newDate)
  }

  const convertDatetimeFormat = (dateISOString: string): string => {
    //Return format 29 Sep, 2023 / 04:45 PM
    if (!dateISOString) return ''
    // Get GMT offset from interface data
    const gmt = parseInt(
      (dataInterface?.timeZone ?? '').replace('GMT', '').replace('+', ''),
    )
    if (gmt === undefined || gmt === null || isNaN(gmt)) return ''
    // Convert the provided ISO string into a UTC-based object
    const utcDate = convertToUTCByTimezone(dateISOString, gmt)
    if (!utcDate) return ''

    const time = {
      hour: utcDate.hour,
      min: utcDate.min,
      sec: utcDate.sec,
    }

    return `${prefixZero(utcDate.date)} ${monthStringFormat(
      utcDate.month,
      true,
    )}, ${utcDate.year} / ${timeStringFormat(time, false, true)}`
  }

  return {
    convertByGMT,
    formatDateByTimezone,
    formatDateSimulation,
    convertToUTCByTimezone,
    revertDateTime,
    convertDateTime,
    convertLocalToProfileZone,
    convertLocalSimulation,
    simulationTimeZone,
    formatSimulationTime,
    simulationOffsetLabel,
    // convertLocalSimulation throws until the interface's time zone has
    // loaded; charts wait for this before converting times
    isSimulationTimeZoneReady: !!dataInterface?.timeZone?.match(
      /GMT([+-])(\d{2}):?(\d{2})?/,
    ),
    formatDateTimeWithoutInterfaceId,
    convertToUTC,
    formatTimeZone,
    extractDayAndMonth,
    convertDatetimeFormat,
    EasyDate,
  }
}

export function validateAndFormatDate(
  dateString: string,
  inputFormat: string,
  outputFormat: string,
  locale?: string, // now optional, any string
): string {
  // 1. Decide which month‐abbr set to use: only "ko-KR" → Korean; everything else → English
  const loc = locale === 'ko-KR' ? 'ko' : 'en'

  // 2. Parsing maps: English three‐letter, Korean “n월”, plus two‐digit numeric
  const monthNames: Record<string, number> = {
    // English
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
    // Korean
    '1월': 1,
    '2월': 2,
    '3월': 3,
    '4월': 4,
    '5월': 5,
    '6월': 6,
    '7월': 7,
    '8월': 8,
    '9월': 9,
    '10월': 10,
    '11월': 11,
    '12월': 12,
  }

  // 3. Formatting arrays for output
  const monthAbbr: Record<'en' | 'ko', string[]> = {
    en: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    ko: [
      '1월',
      '2월',
      '3월',
      '4월',
      '5월',
      '6월',
      '7월',
      '8월',
      '9월',
      '10월',
      '11월',
      '12월',
    ],
  }

  // 4. Build dynamic regex from inputFormat
  const TOKENS: [string, string][] = [
    ['yyyy', '(?<year>\\d{4})'],
    ['MMM', '(?<monthName>[A-Za-z]{3}|\\d{1,2}월)'],
    ['MM', '(?<month>\\d{2})'],
    ['dd', '(?<day>\\d{2})'],
  ]
  let pattern = inputFormat.replace(/([.*+?^${}()|\[\]\\])/g, '\\$1')
  for (const [tok, rgx] of TOKENS) {
    pattern = pattern.replace(tok, rgx)
  }
  const fullRe = new RegExp(`^${pattern}$`, 'i')
  const match = fullRe.exec(dateString)
  if (!match?.groups) return dateString

  // 5. Extract parts
  const { day, month, year, monthName } = match.groups as Record<string, string>
  const d = Number(day),
    y = Number(year)
  let mNum: number

  if (monthName) {
    const key = monthName.toLowerCase()
    if (!(key in monthNames)) return dateString
    mNum = monthNames[key]
  } else {
    mNum = Number(month)
  }

  // 6. Validate ranges (month 1–12, day correct for month, leap‐year Feb)
  if (mNum < 1 || mNum > 12) return dateString
  const dim = [
    31,
    (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ]
  if (d < 1 || d > dim[mNum - 1]) return dateString

  // 7. Zero‐pad and pick abbr
  const dd = String(d).padStart(2, '0')
  const MM = String(mNum).padStart(2, '0')
  const yyyy = String(y)
  const MMM = monthAbbr[loc][mNum - 1]

  // 8. Replace tokens in outputFormat (longer tokens first)
  let result = outputFormat
  for (const [tok, val] of [
    ['yyyy', yyyy],
    ['MMM', MMM],
    ['MM', MM],
    ['dd', dd],
  ] as [string, string][]) {
    result = result.replace(new RegExp(tok, 'g'), val)
  }

  return result
}
